import { css } from '@emotion/react'
import { Button, Heading, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { Ellipsis } from 'components/Ellipsis'
import { useLocalStorage } from 'hooks/useStorage'
import iconBalloonUpdates from 'images/contextual-help-card/icon-balloon-updates.svg'
import { useState } from 'react'

interface ContextualHelpCardProps {
  title: string
  description: React.ReactNode
  children?: React.ReactNode
  image?: string
  imageHeight?: number
  showVerMais?: boolean
  maxLines?: number
  dataTestId?: string
  storageKey?: string
}

export function ContextualHelpCard(props: Readonly<ContextualHelpCardProps>) {
  return props.storageKey ? (
    <StoredContextualHelpCardComponent {...props} storageKey={props.storageKey} />
  ) : (
    <ContextualHelpCardComponent {...props} />
  )
}

function StoredContextualHelpCardComponent(props: Readonly<ContextualHelpCardProps & { storageKey: string }>) {
  const [getStored, setStored] = useLocalStorage<boolean>(props.storageKey, { defaultValue: false })
  const [isDismissed, setDismissed] = useState<boolean>(() => getStored())

  if (isDismissed) return null

  const handleDismiss = () => {
    setDismissed(true)
    setStored(true)
  }

  return <ContextualHelpCardComponent {...props} onDismiss={handleDismiss} />
}

function ContextualHelpCardComponent(props: Readonly<ContextualHelpCardProps & { onDismiss?: () => void }>) {
  const {
    title,
    description,
    children,
    image = iconBalloonUpdates,
    imageHeight,
    showVerMais = true,
    maxLines = 1,
    dataTestId,
    onDismiss,
  } = props
  const theme = useTheme()
  const styles = createStyles(theme)
  const [internalDismissed, setInternalDismissed] = useState(false)
  const [verMais, setVerMais] = useState(false)

  if (internalDismissed) return null

  const handleDismiss = onDismiss ?? (() => setInternalDismissed(true))
  const truncateText = showVerMais && !verMais

  return (
    <Box style={styles.box} dataTestId={dataTestId}>
      <div css={styles.closeButton}>
        <Button skin='ghost' size='small' onClick={handleDismiss}>
          <Icon icon='timesDefault' />
        </Button>
      </div>

      <HFlow hSpacing={0.5}>
        <img src={image} height={imageHeight} alt='Contextual help' />

        <VFlow vSpacing={0.25}>
          <Heading level={4}>{title}</Heading>

          {truncateText ? (
            <Ellipsis showTooltip={false} maxLines={maxLines}>
              {description}
            </Ellipsis>
          ) : (
            <Text>{description}</Text>
          )}

          {showVerMais && (
            <Button skin='ghost' size='small' kind='primary' onClick={() => setVerMais(!verMais)}>
              <Text color='primary'>{verMais ? 'Ver menos' : 'Ver mais'}</Text>
            </Button>
          )}

          {children}
        </VFlow>
      </HFlow>
    </Box>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    width: 100%;
    border: 1px solid ${theme.pallete.gray.c80};
    border-radius: 6px;
    padding: 0.5rem 2.5rem 0.5rem 0.5rem;
    position: relative;
  `,
  closeButton: css`
    position: absolute;
    top: 0;
    right: 0;
  `,
})
